/**
 * POS PIN Setup — Merchant Settings Section
 * Allows restaurant owner to assign 6-digit PINs to employees for POS access.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  KeyRound, Users, Check, X, Loader2, Lock, Unlock,
  ShieldAlert, Copy, RefreshCw, Trash2, ChevronDown
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { accountHeaders } from '@/lib/pwaAccount';

const API = process.env.REACT_APP_BACKEND_URL || '';

const pinHeaders = () => ({ ...accountHeaders(), 'Content-Type': 'application/json' });

export default function POSPinSetup({ token }) {
  const [staff, setStaff] = useState([]);
  const [pinPolicy, setPinPolicy] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [pinValue, setPinValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [unlocking, setUnlocking] = useState(null);

  const headers = pinHeaders();

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/merchant/staff/pins`, { headers: pinHeaders() });
      if (!res.ok) throw new Error('Failed to load staff');
      const data = await res.json();
      setStaff(data.staff || []);
      setPinPolicy(data.pin_policy || 'PIN must be 6 digits');
    } catch (err) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleSetPin = async () => {
    if (!selectedStaffId) { toast.error('Please select an employee'); return; }
    if (!/^\d{6}$/.test(pinValue)) { toast.error('PIN must be exactly 6 digits'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/merchant/staff/pins/${selectedStaffId}/set`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ staff_id: parseInt(selectedStaffId), pin: pinValue }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to set PIN');
      }
      const emp = staff.find(s => s.id === parseInt(selectedStaffId));
      toast.success(`PIN set for ${emp?.name || 'employee'}`);
      setPinValue('');
      setSelectedStaffId('');
      await fetchStaff();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePin = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      const res = await fetch(`${API}/api/merchant/staff/pins/${confirmRemove.id}/reset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mode: 'clear' }),
      });
      if (!res.ok) throw new Error('Failed to remove PIN');
      toast.success(`PIN removed for ${confirmRemove.name}`);
      setConfirmRemove(null);
      await fetchStaff();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRemoving(false);
    }
  };

  const handleUnlock = async (emp) => {
    setUnlocking(emp.id);
    try {
      const res = await fetch(`${API}/api/merchant/staff/pins/${emp.id}/unlock`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Failed to unlock');
      toast.success(`${emp.name} unlocked`);
      await fetchStaff();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUnlocking(null);
    }
  };

  const selectedEmployee = staff.find(s => s.id === parseInt(selectedStaffId));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pos-pin-setup">
      {/* Assign PIN form */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="h-5 w-5 text-emerald-400" />
            <h3 className="font-semibold text-white text-base">Assign POS PIN</h3>
          </div>
          <p className="text-xs text-slate-400">{pinPolicy}</p>

          {/* Employee dropdown */}
          <div>
            <Label className="text-sm text-slate-300 mb-1.5 block">Select Employee</Label>
            <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
              <SelectTrigger
                className="bg-slate-800 border-slate-700 text-white h-11"
                data-testid="pin-employee-select"
              >
                <SelectValue placeholder="Choose an employee..." />
              </SelectTrigger>
              <SelectContent>
                {staff.filter(s => s.is_active).map((emp) => (
                  <SelectItem key={emp.id} value={String(emp.id)}>
                    <span className="flex items-center gap-2">
                      {emp.name}
                      <span className="text-xs text-muted-foreground capitalize">({emp.role})</span>
                      {emp.has_pin && <Check className="h-3 w-3 text-emerald-500" />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected employee info */}
          {selectedEmployee && (
            <div className="bg-slate-800/60 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">{selectedEmployee.name}</span>
                <Badge variant="outline" className="text-[10px] capitalize border-slate-600 text-slate-400">
                  {selectedEmployee.role}
                </Badge>
              </div>
              {selectedEmployee.has_pin && (
                <p className="text-xs text-amber-400 mt-1">
                  This employee already has a PIN — entering a new one will replace it.
                </p>
              )}
            </div>
          )}

          {/* PIN input */}
          <div>
            <Label className="text-sm text-slate-300 mb-1.5 block">6-Digit PIN</Label>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={pinValue}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setPinValue(v);
              }}
              placeholder="Enter 6-digit PIN"
              className="bg-slate-800 border-slate-700 text-white text-center text-lg tracking-[0.5em] font-mono h-12"
              data-testid="pin-input"
            />
            <div className="flex justify-center gap-1 mt-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    i < pinValue.length ? 'bg-emerald-500' : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Submit */}
          <Button
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            disabled={saving || !selectedStaffId || pinValue.length !== 6}
            onClick={handleSetPin}
            data-testid="pin-set-btn"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Setting PIN...</>
            ) : (
              <><KeyRound className="h-4 w-4 mr-2" />Set PIN</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Current PINs table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-400" />
              <h3 className="font-semibold text-white text-base">Employee PIN Status</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchStaff} className="text-slate-400 h-8">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
          </div>

          <div className="space-y-2">
            {staff.filter(s => s.is_active).map((emp) => (
              <div
                key={emp.id}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-800/60 hover:bg-slate-800 transition-colors"
                data-testid={`pin-status-${emp.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    emp.has_pin ? 'bg-emerald-900/50 text-emerald-400' : 'bg-slate-700 text-slate-500'
                  }`}>
                    {emp.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{emp.name}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 capitalize">{emp.role}</span>
                      {emp.has_pin ? (
                        <Badge className="bg-emerald-900/40 text-emerald-400 border-0 text-[10px] h-4">
                          PIN Set
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500 h-4">
                          No PIN
                        </Badge>
                      )}
                      {emp.is_locked && (
                        <Badge className="bg-red-900/40 text-red-400 border-0 text-[10px] h-4">
                          Locked
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {emp.is_locked && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-amber-400 hover:text-amber-300"
                      onClick={() => handleUnlock(emp)}
                      disabled={unlocking === emp.id}
                      data-testid={`pin-unlock-${emp.id}`}
                    >
                      {unlocking === emp.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Unlock className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  {emp.has_pin && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-400 hover:text-red-300"
                      onClick={() => setConfirmRemove(emp)}
                      data-testid={`pin-remove-${emp.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Remove confirmation dialog */}
      <Dialog open={!!confirmRemove} onOpenChange={() => setConfirmRemove(null)}>
        <DialogContent className="max-w-sm" aria-describedby="remove-pin-desc">
          <DialogHeader>
            <DialogTitle>Remove PIN?</DialogTitle>
            <p id="remove-pin-desc" className="text-sm text-muted-foreground">
              Remove the POS PIN for <strong>{confirmRemove?.name}</strong>? They won't be able to log into the POS until a new PIN is set.
            </p>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleRemovePin}
              disabled={removing}
              data-testid="pin-remove-confirm"
            >
              {removing ? 'Removing...' : 'Remove PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
